import React, { useEffect } from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TSewCustomerPartiesProps } from '@/backoffice/features/tenants/components/sew-customer-parties/sew-customer-parties.types';
import { SewCustomerPartyFields } from '@/backoffice/features/tenants/components/sew-customer-parties/sew-customer-party-fields';
import {
  SEW_CUSTOMER_PARTIES,
  SEW_CUSTOMER_PARTIES_MODE,
  SEW_CUSTOMER_PARTIES_MODE_FIELD,
  SEW_SEPARATE_PARTY_KEYS,
} from '@/reseller/sew/metadata/sew-metadata.constants';
import { RadioGroup } from '@/shared/components/ui/form/components/radio-group/radio-group';

const PARTIES_KEY_PREFIX = 'backoffice:sew.onboarding.customer_parties';

export const SewCustomerParties: React.FC<TSewCustomerPartiesProps> = ({
  allowSeparateParties,
  hintSuffix,
}) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const mode = useWatch({ control, name: SEW_CUSTOMER_PARTIES_MODE_FIELD });

  const hasSeparateParties =
    Boolean(allowSeparateParties) &&
    mode === SEW_CUSTOMER_PARTIES_MODE.separate;

  // Leaving Customer Billing takes the party roles with it, so the choice and
  // any ship-to/bill-to input collected under it are dropped.
  useEffect(() => {
    if (allowSeparateParties) return;

    setValue(SEW_CUSTOMER_PARTIES_MODE_FIELD, SEW_CUSTOMER_PARTIES_MODE.same);
    SEW_SEPARATE_PARTY_KEYS.forEach((key) => {
      setValue(`metaData.${key}`, '');
    });
  }, [allowSeparateParties, setValue]);

  const modeOptions = [
    {
      value: SEW_CUSTOMER_PARTIES_MODE.same,
      label: t(`${PARTIES_KEY_PREFIX}.same`),
      description: t(`${PARTIES_KEY_PREFIX}.same_hint`),
    },
    {
      value: SEW_CUSTOMER_PARTIES_MODE.separate,
      label: t(`${PARTIES_KEY_PREFIX}.separate`),
      description: t(`${PARTIES_KEY_PREFIX}.separate_hint`),
    },
  ];

  const renderSoldToOnly = () => {
    const [soldTo] = SEW_CUSTOMER_PARTIES;

    return (
      <SewCustomerPartyFields
        party={soldTo}
        nameLabel={t('backoffice:sew.onboarding.customer')}
        idLabel={t('backoffice:sew.onboarding.sew_customer_id')}
        nameHint={
          hintSuffix
            ? t(`backoffice:sew.onboarding.customer_hint_${hintSuffix}`)
            : undefined
        }
        idHint={
          hintSuffix
            ? t(`backoffice:sew.onboarding.sew_customer_id_hint_${hintSuffix}`)
            : undefined
        }
      />
    );
  };

  const renderAllParties = () =>
    SEW_CUSTOMER_PARTIES.map((party) => (
      <SewCustomerPartyFields
        key={party.party}
        party={party}
        title={t(`${PARTIES_KEY_PREFIX}.${party.party}.title`)}
        nameLabel={t('backoffice:sew.onboarding.customer')}
        idLabel={t(`${PARTIES_KEY_PREFIX}.customer_number`)}
        namePlaceholder={t(
          `${PARTIES_KEY_PREFIX}.${party.party}.name_placeholder`,
        )}
        idPlaceholder={t(
          `${PARTIES_KEY_PREFIX}.${party.party}.number_placeholder`,
        )}
      />
    ));

  return (
    <>
      {allowSeparateParties && (
        <RadioGroup<FieldValues>
          name={SEW_CUSTOMER_PARTIES_MODE_FIELD}
          label={t(`${PARTIES_KEY_PREFIX}.question`)}
          hint={t(`${PARTIES_KEY_PREFIX}.hint`)}
          required
          optionDirection="column"
          options={modeOptions}
          defaultValue={SEW_CUSTOMER_PARTIES_MODE.same}
        />
      )}
      {hasSeparateParties ? renderAllParties() : renderSoldToOnly()}
    </>
  );
};
